import React, { useState } from "react";
import { Box, Typography, Button, LinearProgress, Alert, Fab, Tooltip } from "@mui/material";
import { AutoFixHigh, Replay, Refresh } from "@mui/icons-material";
import { observer } from "mobx-react";
import { toJS } from "mobx";
import useStore from "../../../helpers/useStore";
import { MutationDraftContext } from "../../../stores/MutationDraft/mutationDraft.provider";
import DraftRequestCard from "./DraftRequestCard";
import { useCalendarDraftActions } from "../../../hooks/useCalendarDraftActions";
import ChatMessageBox from "../../../molecules/ChatMessageBox";
import { AgentChatContext } from "../../../stores/AgentChat/agentChat.provider";
import { UserContext } from "../../../stores/User";
import { MobiliAvatar } from "../../../atoms/MobiliAvatar";
import { useReschedulingAgent } from "../../../hooks/useReschedulingAgent";
import BackplanModal from "../../../molecules/Calendar/BackplanModal";
import { AgentName } from "../../../stores/MutationDraft/mutationDraftStore";

// ─── Config per agent ────────────────────────────────────────────────────────

const AGENT_CONFIG: Record<
  AgentName,
  {
    title: string;
    idleDescription: string;
    startLabel: string;
    loadingText: string;
    restartLabel: string;
  }
> = {
  rescheduling: {
    title: "Planungs-Agent",
    idleDescription: "Der Agent sucht automatisch Terminvorschläge für den ausgewählten Patienten.",
    startLabel: "Agent starten",
    loadingText: "Terminvorschläge werden berechnet…",
    restartLabel: "Neu berechnen",
  },
  backplanning: {
    title: "Backplanning-Agent",
    idleDescription: "Der Backplanning-Agent plant Dauertermine automatisch in den Kalender ein.",
    startLabel: "Backplanning starten",
    loadingText: "Dauertermine werden verarbeitet…",
    restartLabel: "Neu starten",
  },
};

// ─── Props ───────────────────────────────────────────────────────────────────

interface AgentDrawerContentProps {
  /** Required for rescheduling agent */
  patientId?: number;
}

// ─── Component ───────────────────────────────────────────────────────────────

const AgentDrawerContent: React.FC<AgentDrawerContentProps> = observer(({ patientId }) => {
  const { user } = useStore(UserContext);
  const mutationStoreRegistry = useStore(MutationDraftContext);
  const chatStoreRegistry = useStore(AgentChatContext);

  const draftStore = mutationStoreRegistry.getDefaultStore();
  const chatStore = chatStoreRegistry.getDefaultStore();
  const { clearAll, isReady: isMutationReady } = useCalendarDraftActions();
  const { startRescheduling } = useReschedulingAgent(patientId);

  const [backplanModalOpen, setBackplanModalOpen] = useState(false);

  // Read agent state — always show the active agent regardless of calendar tab
  const { status, errorMessage, progress, agentName } = draftStore.agent.state;
  const activeAgent = agentName ?? "rescheduling";
  const isLoading = status === "running";

  const storeRequests = draftStore.allRequests;
  const storeRequestCount = storeRequests.length;

  const config = AGENT_CONFIG[activeAgent];

  // ─── Handlers ──────────────────────────────────────────────────────────────

  const handleStartRescheduling = () => {
    startRescheduling();
  };

  const handleStartBackplanning = () => {
    setBackplanModalOpen(true);
  };

  const handleRestart = () => {
    if (activeAgent === "rescheduling") {
      startRescheduling();
    } else {
      setBackplanModalOpen(true);
    }
  };

  const handleReset = () => {
    clearAll();
    draftStore.agent.reset();
    chatStore.clear();
  };

  const handleResendRejected = () => {
    alert(
      "feature under development: abgelehnte Termine werden neu berechnet und in der Liste aktualisiert",
    );
  };

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <>
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
        {/* Header */}
        <Box sx={{ px: 1.5, py: 1, borderBottom: "1px solid", borderColor: "divider" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <AutoFixHigh color="primary" fontSize="small" />
            <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: "0.9rem", flex: 1 }}>
              {config.title}
            </Typography>
          </Box>
        </Box>

        {/* Progress bar */}
        {isLoading && (
          <LinearProgress
            variant={progress > 0 ? "determinate" : "indeterminate"}
            value={progress}
            sx={{ flexShrink: 0 }}
          />
        )}

        {/* Body — scrollable */}
        <Box sx={{ flex: 1, overflow: "auto", px: 1.5, py: 1 }}>
          {/* Idle state — show both start buttons */}
          {status === "idle" && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 2,
                pt: 4,
              }}
            >
              <AutoFixHigh sx={{ fontSize: 48, color: "action.disabled" }} />
              <Typography variant="body2" color="text.secondary" textAlign="center">
                Agent starten, um Terminvorschläge automatisch zu berechnen.
              </Typography>
              <Button
                variant="contained"
                fullWidth
                startIcon={<AutoFixHigh />}
                onClick={handleStartRescheduling}
                disabled={!patientId}
              >
                Planung starten
              </Button>
              {!patientId && (
                <Typography variant="caption" color="error">
                  Bitte zuerst einen Patienten auswählen.
                </Typography>
              )}
              <Button
                variant="outlined"
                fullWidth
                startIcon={<AutoFixHigh />}
                onClick={handleStartBackplanning}
              >
                Backplanning starten
              </Button>
            </Box>
          )}

          {/* Loading state */}
          {status === "running" && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 1,
                pt: 4,
              }}
            >
              <Typography variant="body2" color="text.secondary">
                {config.loadingText}
              </Typography>
              <Typography variant="caption" color="text.disabled">
                {Math.round(progress)}%
              </Typography>
            </Box>
          )}

          {/* Error state */}
          {status === "error" && (
            <Box sx={{ pt: 2 }}>
              <Alert severity="error" sx={{ mb: 2, fontSize: "0.75rem" }}>
                {errorMessage || "Ein Fehler ist aufgetreten."}
              </Alert>
              <Button size="small" startIcon={<Replay />} onClick={handleRestart}>
                Erneut versuchen
              </Button>
            </Box>
          )}

          {/* Chat messages */}
          {chatStore.hasMessages && (
            <Box sx={{ mb: 1, maxHeight: 500, overflow: "hidden" }}>
              <ChatMessageBox
                messages={toJS(chatStore.messages)}
                userAvatar={
                  user ? (
                    <MobiliAvatar user={user} sx={{ width: 22, height: 22, fontSize: "0.6rem" }} />
                  ) : undefined
                }
                slotProps={{
                  bubbleBox: { px: 1, py: 0.5 },
                  content: { fontSize: "0.7rem", textAlign: "left" },
                  timestamp: { fontSize: "0.55rem" },
                  avatarSize: 22,
                }}
              />
            </Box>
          )}

          {/* Draft request cards */}
          {storeRequestCount > 0 && (
            <>
              <Typography
                variant="overline"
                sx={{
                  fontSize: "0.6rem",
                  color: "text.secondary",
                  display: "block",
                  mt: 1,
                  mb: 0.5,
                }}
              >
                Vorschläge ({storeRequestCount})
              </Typography>
              {storeRequests.map((req) => (
                <DraftRequestCard key={req.id} requestId={req.id} />
              ))}
            </>
          )}
        </Box>

        {/* Footer */}
        <Box sx={{ flexShrink: 0, pb: 9 }}>
          {/* Rejection resend — rescheduling only */}
          {activeAgent === "rescheduling" && draftStore.hasRejections && (
            <Box sx={{ display: "flex", justifyContent: "flex-end", px: 1.5, pt: 0.75 }}>
              <Tooltip title={`${draftStore.rejectedIds.length} abgelehnte Termine neu berechnen`}>
                <Fab
                  color="warning"
                  size="small"
                  onClick={handleResendRejected}
                  disabled={!isMutationReady}
                >
                  <Refresh />
                </Fab>
              </Tooltip>
            </Box>
          )}

          {/* Restart button */}
          {status === "completed" && (
            <Box
              sx={{
                px: 1.5,
                py: 1,
                borderTop: "1px solid",
                borderColor: "divider",
                display: "flex",
                gap: 1,
              }}
            >
              <Button
                fullWidth
                variant="outlined"
                size="small"
                startIcon={<Replay />}
                onClick={handleRestart}
              >
                {config.restartLabel}
              </Button>
            </Box>
          )}

          {/* Reset link */}
          {status === "completed" && (
            <Box sx={{ px: 1.5, pb: 0.75, textAlign: "center" }}>
              <Button
                size="small"
                color="inherit"
                onClick={handleReset}
                sx={{ fontSize: "0.65rem" }}
              >
                Zurücksetzen
              </Button>
            </Box>
          )}
        </Box>
      </Box>

      {/* Backplan modal */}
      <BackplanModal open={backplanModalOpen} onClose={() => setBackplanModalOpen(false)} />
    </>
  );
});

export default AgentDrawerContent;
